import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, Activity, HeartPulse, Gift, ChevronRight, X } from "lucide-react";

const STEPS = [
  {
    accent: "#d7ff00",
    icon: Sparkles,
    title: "Welcome to Coach JP",
    body: (name) => (
      <>
        <p>You're in, <span className="text-[#d7ff00] font-medium">{name}</span>. Real coaching. No generic plans. No copy-paste workouts.</p>
        <p className="mt-3 text-[#bbb] text-sm">A 60-second tour, then you're off.</p>
      </>
    ),
    cta: "Show me around",
  },
  {
    accent: "#d7ff00",
    icon: Activity,
    title: "Today's session, always front & centre",
    body: () => (
      <>
        <p>Every morning, your <span className="text-[#d7ff00]">today's session</span> appears at the top of Home.</p>
        <ul className="mt-3 space-y-1.5 text-sm text-[#ddd]">
          <li>· Tap <span className="text-[#d7ff00]">Open Training Plan</span> for full details</li>
          <li>· Mark complete after each run — log your splits, pace, and how it felt</li>
          <li>· Coach JP sees every note you leave</li>
        </ul>
      </>
    ),
    cta: "Next",
  },
  {
    accent: "#71c4ff",
    icon: HeartPulse,
    title: "Recovery drives performance",
    body: () => (
      <>
        <p>Log your <span className="text-[#71c4ff]">HRV, sleep, and resting HR</span> daily (30 seconds).</p>
        <p className="mt-3 text-sm text-[#bbb]">Your <span className="text-[#71c4ff]">readiness score</span> tells you when to push and when to back off. Green = go. Yellow = moderate. Red = recover.</p>
      </>
    ),
    cta: "Next",
  },
  {
    accent: "#d7ff00",
    icon: Gift,
    title: "Give a week. Get a week.",
    body: () => (
      <>
        <p>Invite a friend with your referral code — you <span className="text-[#d7ff00]">both</span> get a free week on top of your subscription.</p>
        <p className="mt-3 text-sm text-[#bbb]">No limits. Bring the crew.</p>
      </>
    ),
    cta: "Let's run",
  },
];

export default function WelcomeModal({ user, onClose }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;
  const accent = current.accent;

  const finish = async () => {
    try { await api.post("/me/welcomed"); } catch { /* noop */ }
    onClose();
    if (!user?.profile_complete) {
      navigate("/onboarding");
    }
  };
  const next = () => isLast ? finish() : setStep(step + 1);

  return createPortal((
    <div className="fixed inset-0 z-[60] bg-black/90 overflow-y-auto" data-testid="welcome-modal" onClick={finish}>
      <div className="min-h-full flex items-center justify-center p-4">
        <div
          className="bg-[#0a0a0a] border w-full max-w-md p-5 sm:p-8 relative my-6"
          style={{ borderColor: accent, boxShadow: `0 0 60px ${accent}33` }}
          onClick={e => e.stopPropagation()}
        >
        <button onClick={finish} className="absolute top-4 right-4 text-[#666] hover:text-white" data-testid="welcome-skip" aria-label="Skip">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 font-mono-jp text-[10px] uppercase tracking-widest" style={{ color: accent }}>
          <Icon className="w-3.5 h-3.5" /> STEP {step + 1} / {STEPS.length}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl uppercase mt-2 leading-tight">{current.title}</h2>
        <div className="mt-4 text-white leading-relaxed">
          {current.body(user?.name?.split(" ")[0] || "Runner")}
        </div>

        {/* Step dots */}
        <div className="mt-6 flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 transition-colors"
              style={{ background: i <= step ? accent : "#1f1f1f" }}
            />
          ))}
        </div>

        <button
          onClick={next}
          className="mt-5 w-full text-black font-display tracking-wider uppercase py-3 hover:opacity-90 flex items-center justify-center gap-2"
          style={{ background: accent }}
          data-testid="welcome-next"
        >
          {current.cta} <ChevronRight className="w-4 h-4" />
        </button>
        {!isLast && (
          <button onClick={finish} className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-[#666] hover:text-white" data-testid="welcome-skip-all">
            Skip tour
          </button>
        )}
        </div>
      </div>
    </div>
  ), document.body);
}
